<template>
    <div class="space-y-8">
        <div class="space-y-5">
            <div>
                <h1
                    class="flex items-center gap-3 text-3xl font-bold text-gray-900 sm:text-4xl"
                >
                    <span>{{ title }}</span>
                    <i
                        v-if="staffActivationRequired"
                        class="pi pi-arrow-right text-amber-700 shrink-0 text-3xl"
                        aria-hidden="true"
                    />
                </h1>
                <div
                    class="to-transparent mt-4 h-[3px] bg-gradient-to-r from-primary-600 to-50%"
                    aria-hidden="true"
                />
            </div>
            <content-reset
                v-if="customMarkup !== ''"
                :body="customMarkup"
                class="text-xl text-gray-700"
            />
            <p
                v-else
                class="text-xl leading-relaxed text-gray-700"
            >
                {{ defaultMessage }}
            </p>
        </div>

        <div class="space-y-5">
            <div
                v-for="contract in contracts"
                :key="contract.referenceNumber"
                class="flex items-center justify-between gap-5"
            >
                <div class="min-w-0 flex-1 space-y-2">
                    <div
                        class="break-words text-xl font-semibold text-gray-900"
                    >
                        {{ contract.localizedName }}
                    </div>
                    <div class="text-gray-600">
                        {{ t('checkoutSuccess.referenceNumber') }}
                        <span class="font-medium text-gray-900">
                            {{ `#${contract.referenceNumber}` }}
                        </span>
                    </div>
                </div>
                <div
                    v-if="
                        !staffActivationRequired &&
                        contract.savedDuesTotal !== null &&
                        contract.terms.recurringConfiguration !== null
                    "
                    class="text-right text-lg font-medium text-gray-900"
                >
                    {{
                        formatRecurringTotalString(
                            new Decimal(contract.savedDuesTotal),
                            contract.terms.recurringConfiguration
                                .billingInterval,
                            contract.plan.terms.billingCycleString ?? ''
                        )
                    }}
                </div>
            </div>
        </div>

        <section
            v-if="
                !staffActivationRequired &&
                invoice !== null &&
                invoice.lineItems.length > 0
            "
        >
            <ul class="m-0 list-none divide-y p-0">
                <li
                    v-for="(item, index) in invoice.lineItems"
                    :key="index"
                    class="flex items-start gap-5 py-3 sm:items-center"
                >
                    <div class="min-w-0 flex-1 lg:mr-16">
                        <div
                            class="break-words text-xl font-medium text-gray-900"
                        >
                            {{ item.productVariant.description }}
                        </div>
                        <div class="mt-2 text-gray-900">
                            {{
                                t('checkoutSuccess.kioskQuantityPrice', {
                                    quantity: item.quantity,
                                    price: formatCurrency(item.unitPrice)
                                })
                            }}
                        </div>
                    </div>
                    <div class="shrink-0 text-lg font-medium text-gray-900">
                        {{ formatCurrency(item.extendedPrice) }}
                    </div>
                </li>
            </ul>
            <dl
                class="space-y-3 border-t pt-4 text-gray-900 lg:ml-auto lg:w-1/2"
            >
                <div class="flex justify-between gap-5">
                    <dt>{{ t('checkout.subtotal') }}</dt>
                    <dd class="shrink-0 text-lg font-medium">
                        {{ formatCurrency(invoice.subTotal) }}
                    </dd>
                </div>
                <div
                    v-if="new Decimal(invoice.salesTaxInclusive).isNotZero()"
                    class="flex justify-between gap-5"
                >
                    <dt>{{ t('checkout.inclusiveTax') }}</dt>
                    <dd class="shrink-0 text-lg font-medium">
                        {{ formatCurrency(invoice.salesTaxInclusive) }}
                    </dd>
                </div>
                <div class="flex justify-between gap-5">
                    <dt>{{ t('checkout.tax') }}</dt>
                    <dd class="shrink-0 text-lg font-medium">
                        {{ formatCurrency(invoice.salesTaxExclusive) }}
                    </dd>
                </div>
                <div class="flex justify-between gap-5 pt-3">
                    <dt class="font-medium">
                        {{ t('checkoutSuccess.kioskTotalPrice') }}
                    </dt>
                    <dd class="shrink-0 text-lg font-bold">
                        {{ formatCurrency(invoice.total) }}
                    </dd>
                </div>
                <template v-if="invoice.friendlyBindings.length > 0">
                    <template
                        v-for="(binding, index) in invoice.friendlyBindings"
                        :key="index"
                    >
                        <div
                            v-if="!binding.isSubItem"
                            class="flex justify-between gap-5"
                        >
                            <dt class="font-medium">
                                {{ t('invoice.payment') }}
                            </dt>
                            <dd class="shrink-0 text-lg font-bold">
                                {{ binding.amountAndFee }}
                            </dd>
                        </div>
                        <div v-else-if="binding.fee !== ''">
                            <dt class="sr-only">{{ t('invoice.payment') }}</dt>
                            <dd
                                class="text-xs text-gray-600"
                                v-html="binding.fee"
                            />
                        </div>
                    </template>
                </template>
                <div
                    v-if="new Decimal(invoice.balanceRemaining).isNotZero()"
                    class="flex justify-between gap-5"
                >
                    <dt class="font-medium">
                        {{ t('checkoutSuccess.kioskRemainingBalance') }}
                    </dt>
                    <dd class="shrink-0 text-lg font-bold">
                        {{ formatCurrency(invoice.balanceRemaining) }}
                    </dd>
                </div>
            </dl>
        </section>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { getFragmentData } from '~/resources/graphql'
import {
    type KioskSuccessData,
    KioskContractFragment,
    KioskInvoiceFragment
} from '~/composables/use-kiosk-success'
import { ContractStatus } from '../../../resources/app/generated/enums'
import { Decimal } from '~/composables/support/decimal'
import { formatCurrency } from '~/composables/support/localization'
import { formatRecurringTotalString } from '~/composables/contract-question-formatting'
import ContentReset from '~/components/ContentReset.vue'

const props = defineProps<{
    checkoutResultData: KioskSuccessData
    completedMessage: string
    staffActivationRequiredMessage: string
}>()

const { t } = useI18n({ useScope: 'global' })
const contracts = computed(() =>
    getFragmentData(KioskContractFragment, props.checkoutResultData.contracts)
)
const staffActivationRequired = computed(() =>
    contracts.value.some(
        contract =>
            contract.currentStatus === ContractStatus[ContractStatus.DRAFT]
    )
)
const customMarkup = computed(() =>
    staffActivationRequired.value
        ? props.staffActivationRequiredMessage
        : props.completedMessage
)
const invoice = computed(() =>
    getFragmentData(KioskInvoiceFragment, props.checkoutResultData.invoice)
)
const hasPricing = computed(() => {
    return (
        (invoice.value?.lineItems.length ?? 0) > 0 ||
        contracts.value.some(
            contract =>
                contract.savedFeesTotal !== null ||
                contract.savedDuesTotal !== null
        )
    )
})
const title = computed(() => {
    if (staffActivationRequired.value) {
        return t('checkoutSuccess.kioskStaffActivationRequiredTitle')
    }
    return hasPricing.value
        ? t('checkoutSuccess.kioskPurchaseCompletedTitle')
        : t('checkoutSuccess.kioskCompletedTitle')
})
const defaultMessage = computed(() => {
    return staffActivationRequired.value
        ? t('checkoutSuccess.kioskStaffActivationRequiredMessage')
        : t('checkoutSuccess.kioskCompletedMessage')
})
</script>
